/**
 * Email Automation Contacts Page
 * Route: /marketing/automations/email/:id/contacts
 *
 * Faithful replica of PHP automation-contacts.php — contact list with status badges,
 * step progress, open/click stats, search, and per-contact stats modal.
 *
 * Every box on the stats page links here with its filters in the URL
 * (?status=, ?lead=, ?stepId=, ?outcome=), which seed the controls below.
 *
 * Step + Outcome work as on SmsAutomationContactsPage: they are about SEND rows,
 * the tries behind one step's numbers on the stats page, while "Filter by
 * status" is where the contact stands now. Both are kept in the URL
 * (?stepId=&outcome=) so a filtered view survives a refresh. Step and outcome
 * counts are the step cards' own (send rows, retries included), so they can run
 * ahead of the number of contacts listed.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Search, Mail, MousePointerClick, User } from 'lucide-react';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { FilterSelect } from '@/components/ui/FilterSelect';
import type { FilterSelectOption } from '@/components/ui/FilterSelect';
import * as marketingSvc from '@/services/marketing.service';
import type { AutomationContact, AutomationContactStatsData, AutomationStepStats, EmailAutomationContactCounts, EmailStepOutcome } from '@/services/marketing.service';
import { EmailAutomationStopNotice } from '@/components/marketing/EmailAutomationStopNotice';
import { EmailAutomationSendHistory, SEND_STATUS_BADGE, sendShowsReason, sendStatusLabel } from '@/components/marketing/EmailAutomationSendHistory';
import { ContactLeadCell, ContactLeadFilter, ContactLeadOpener, leadFilterFromUrl, leadParam, useContactLeadModal } from '@/components/marketing/ContactLeads';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { cn } from '@/lib/utils';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_BADGE_CLASS: Record<string, string> = {
  active: 'eac-badge-active',
  completed: 'eac-badge-completed',
  removed: 'eac-badge-removed',
  unsubscribed: 'eac-badge-unsubscribed',
  invalid: 'eac-badge-invalid',
  failed: 'eac-badge-failed',
};

// The SMS Contacts page's labels. 'failed' is spelled out: a bare "Failed"
// reads as "this contact had a failed send", a far more common thing.
const STATUS_LABELS: Record<string, string> = {
  invalid: 'Invalid',
  failed: 'Stopped after retries',
};

const EMPTY_COUNTS: EmailAutomationContactCounts = {
  total: 0, active: 0, completed: 0, removed: 0, unsubscribed: 0, invalid: 0, failed: 0,
};

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? capitalize(status);
}

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '0';
  return n.toLocaleString();
}

function fmtDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${h}:${m} ${ampm}`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** A step card, live or removed: a removed one carries the order it had. */
type StepOption = AutomationStepStats & { previousStepOrder?: number };

/** How the filtered-list summary reads each outcome: "with a step 2 send that bounced". */
const OUTCOME_PHRASE: Record<EmailStepOutcome, string> = {
  sent: 'that went out',
  delivered: 'that was delivered',
  opened: 'that was opened',
  clicked: 'that was clicked',
  bounced: 'that bounced',
  failed: 'that failed',
  unsubscribed: 'they unsubscribed from',
  spam: 'they marked as spam',
  replied: 'they replied to',
};

function stepLabel(step: StepOption): string {
  return step.previousStepOrder ? `removed step ${step.previousStepOrder}` : `step ${step.stepOrder}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function EmailAutomationContactsPage() {
  const { id } = useParams<{ id: string }>();
  const automationId = Number(id);
  const userTz = useUserTimezone();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '');
  const [leadFilter, setLeadFilter] = useState(() => leadFilterFromUrl(searchParams.get('lead')));
  const [stepFilter, setStepFilter] = useState(() => searchParams.get('stepId') ?? '');
  const [outcomeFilter, setOutcomeFilter] = useState(() => searchParams.get('outcome') ?? '');
  const perPage = 25;
  const queryClient = useQueryClient();

  // State and URL move together, so a filtered view survives a refresh.
  const applySendFilters = useCallback((nextStepId: string, nextOutcome: string) => {
    setStepFilter(nextStepId);
    setOutcomeFilter(nextOutcome);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (nextStepId) next.set('stepId', nextStepId); else next.delete('stepId');
    if (nextOutcome) next.set('outcome', nextOutcome); else next.delete('outcome');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Contact stats modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<AutomationContactStatsData | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Bare call on purpose — see AppointmentsPage: the hook has to keep
  // running, and nothing reads its result any more.
  useQuery({
    queryKey: ['automation-detail', automationId],
    queryFn: () => marketingSvc.getAutomation(automationId),
    enabled: automationId > 0,
  });

  // The steps and their cards' numbers, for the Step and Outcome options: the
  // same per-step counts the stats page shows (all time, not its date range).
  const { data: statsData } = useQuery({
    queryKey: ['automation-contacts-steps', automationId],
    queryFn: () => marketingSvc.getAutomationStats(automationId),
    enabled: automationId > 0,
  });

  const { data: contactsData, isLoading } = useQuery({
    queryKey: ['automation-contacts', automationId, search, page, statusFilter, leadFilter, stepFilter, outcomeFilter],
    queryFn: () => marketingSvc.listAutomationContacts(automationId, {
      search: search || undefined,
      page,
      perPage,
      status: statusFilter || undefined,
      lead: leadParam(leadFilter),
      stepId: stepFilter ? Number(stepFilter) : undefined,
      outcome: (outcomeFilter || undefined) as EmailStepOutcome | undefined,
    }),
    enabled: automationId > 0,
  });

  const contacts = contactsData?.data ?? [];
  const meta = contactsData?.meta;
  const paginationTotal = meta?.total ?? 0;
  const counts = contactsData?.contactCounts ?? EMPTY_COUNTS;
  const leadCount = contactsData?.leadCount;
  const totalSteps = contactsData?.totalSteps ?? 0;

  useEffect(() => {
    document.body.style.overflow = modalOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [modalOpen]);

  const openContactStats = useCallback(async (row: AutomationContact) => {
    setModalOpen(true);
    setModalLoading(true);
    setModalData(null);
    try {
      const data = await marketingSvc.getAutomationContactStats(automationId, row.contactId);
      setModalData(data);
    } catch {
      setModalData(null);
    }
    setModalLoading(false);
  }, [automationId]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setModalData(null);
  }, []);

  // The lead modal opens over the list; its Spam / Trash / Convert can change
  // what the Lead column and the open contact show, so both reload after it.
  const openContactId = modalData?.contact.contactId ?? null;
  const leadModal = useContactLeadModal(useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['automation-contacts', automationId] });
    if (openContactId != null) {
      marketingSvc.getAutomationContactStats(automationId, openContactId).then(setModalData).catch(() => undefined);
    }
  }, [queryClient, automationId, openContactId]));

  // ── Status filter options with counts (dot colours mirror the row badges) ──
  const FILTER_OPTIONS = useMemo<FilterSelectOption[]>(() => [
    { label: 'All Contacts', value: '', count: counts.total, color: '#3b82f6', textColor: '#1d4ed8' },
    { label: 'Active', value: 'active', count: counts.active, color: '#22c55e', textColor: '#15803d' },
    { label: 'Completed', value: 'completed', count: counts.completed, color: '#0ea5e9', textColor: '#0369a1' },
    { label: 'Removed', value: 'removed', count: counts.removed, color: '#ef4444', textColor: '#b91c1c' },
    { label: 'Unsubscribed', value: 'unsubscribed', count: counts.unsubscribed, color: '#f59e0b', textColor: '#b45309' },
    { label: 'Invalid', value: 'invalid', count: counts.invalid, color: '#dc2626', textColor: '#991b1b' },
    { label: 'Stopped after retries', value: 'failed', count: counts.failed, color: '#94a3b8', textColor: '#475569' },
  ], [counts]);

  // Steps to filter by, keyed on stepId. Removed steps come after the live
  // ones: their sends still exist, and the stats page still shows their cards.
  const stepStats = useMemo<StepOption[]>(
    () => [...(statsData?.steps ?? []), ...(statsData?.removedSteps ?? [])],
    [statsData],
  );
  const STEP_OPTIONS = useMemo<FilterSelectOption[]>(() => [
    { label: 'All steps', value: '', color: '#3b82f6', textColor: '#1d4ed8' },
    ...stepStats.map((s) => ({
      label: s.previousStepOrder ? `Removed step (was ${s.previousStepOrder})` : `Step ${s.stepOrder}`,
      value: String(s.stepId),
      count: s.attempts,
      color: s.previousStepOrder ? '#94a3b8' : '#8b5cf6',
      textColor: s.previousStepOrder ? '#475569' : '#6d28d9',
    })),
  ], [stepStats]);

  const selectedStep = useMemo(
    () => stepStats.find((s) => String(s.stepId) === stepFilter),
    [stepStats, stepFilter],
  );

  // Counts are the stats page's own numbers for what is picked: the selected
  // step's card, or with no step the boxes at the top of the page. Sent is
  // what SendGrid accepted, Failed the failed tries, Bounced bounces and drops.
  const outcomeCounts = useMemo<Partial<Record<EmailStepOutcome | 'any', number>>>(() => {
    if (selectedStep) {
      return {
        any: selectedStep.attempts, sent: selectedStep.sent, delivered: selectedStep.delivered,
        opened: selectedStep.opens, clicked: selectedStep.clicks, bounced: selectedStep.bounces,
        failed: selectedStep.failedAttempts, replied: selectedStep.replies,
      };
    }
    const all = statsData?.sends;
    if (stepFilter || !all) return {};
    return {
      sent: all.totalSent, delivered: all.delivered, opened: all.opens, clicked: all.clicks,
      bounced: all.bounces, failed: all.failedAttempts, unsubscribed: all.unsubscribes,
      spam: all.spamReports, replied: all.replies,
    };
  }, [selectedStep, stepFilter, statsData]);

  const OUTCOME_OPTIONS = useMemo<FilterSelectOption[]>(() => [
    { label: 'Any outcome', value: '', count: outcomeCounts.any, color: '#3b82f6', textColor: '#1d4ed8' },
    { label: 'Sent', value: 'sent', count: outcomeCounts.sent, color: '#0ea5e9', textColor: '#0369a1' },
    { label: 'Delivered', value: 'delivered', count: outcomeCounts.delivered, color: '#22c55e', textColor: '#15803d' },
    { label: 'Opened', value: 'opened', count: outcomeCounts.opened, color: '#00aff0', textColor: '#0369a1' },
    { label: 'Clicked', value: 'clicked', count: outcomeCounts.clicked, color: '#8b5cf6', textColor: '#6d28d9' },
    { label: 'Replied', value: 'replied', count: outcomeCounts.replied, color: '#17a2b8', textColor: '#0e7490' },
    { label: 'Bounced', value: 'bounced', count: outcomeCounts.bounced, color: '#f59e0b', textColor: '#b45309' },
    { label: 'Unsubscribed', value: 'unsubscribed', count: outcomeCounts.unsubscribed, color: '#f97316', textColor: '#c2410c' },
    { label: 'Spam report', value: 'spam', count: outcomeCounts.spam, color: '#be123c', textColor: '#9f1239' },
    { label: 'Failed', value: 'failed', count: outcomeCounts.failed, color: '#dc2626', textColor: '#991b1b' },
  ], [outcomeCounts]);

  // ── Table columns ──
  const columns: DataTableColumn<AutomationContact>[] = useMemo(() => [
    {
      key: 'email',
      header: 'Email',
      sortable: true,
    },
    {
      key: 'firstName',
      header: 'Name',
      render: (row) => {
        const name = [row.firstName, row.lastName].filter(Boolean).join(' ');
        return <span>{name || '—'}</span>;
      },
      sortable: true,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => (
        <span className={`eac-badge ${STATUS_BADGE_CLASS[row.status] || 'eac-badge-default'}`}>
          {statusLabel(row.status)}
        </span>
      ),
    },
    {
      key: 'currentStepOrder',
      header: 'Step',
      sortable: true,
      render: (row) => {
        if (row.status === 'completed') {
          return <span style={{ color: '#28a745', fontWeight: 600 }}>Complete</span>;
        }
        return <span>{row.currentStepOrder} / {row.totalSteps || totalSteps}</span>;
      },
    },
    // Only while scoped to one step: what that step did for this contact.
    // Without it the filtered list says who is behind a number but not why.
    ...(stepFilter ? [{
      key: 'stepSend',
      header: selectedStep ? `${capitalize(stepLabel(selectedStep))} outcome` : 'Step outcome',
      render: (row: AutomationContact) => {
        const s = row.stepSend;
        if (!s || !s.deliveryStatus) return <span className="text-gray-400">—</span>;
        // The badge is the try that matched the outcome. When the step later
        // ended differently, a failure whose retry went through, say so.
        const endedDifferently = s.latestDeliveryStatus != null && s.latestDeliveryStatus !== s.deliveryStatus;
        const engaged = [s.opened && 'opened', s.clicked && 'clicked'].filter(Boolean).join(', ');
        return (
          <div>
            <span
              className={cn(
                'inline-block whitespace-nowrap rounded-xl px-2.5 py-0.5 text-xs font-semibold',
                SEND_STATUS_BADGE[s.deliveryStatus] ?? 'bg-[#e9ecef] text-[#666]',
              )}
            >
              {sendStatusLabel(s.deliveryStatus)}
            </span>
            {s.attempts > 1 && (
              <span className="eac-pill" title="Each try is its own send row">{s.attempts} tries</span>
            )}
            {engaged && <span className="ml-1.5 text-xs text-gray-500">{engaged}</span>}
            {endedDifferently && (
              <div className="mt-0.5 text-xs text-gray-500">later {s.latestDeliveryStatus}</div>
            )}
            {s.reason && sendShowsReason(s.deliveryStatus) && (
              <div className="mt-0.5 max-w-xs truncate text-xs text-red-600" title={s.reason}>{s.reason}</div>
            )}
          </div>
        );
      },
    } as DataTableColumn<AutomationContact>] : []),
    {
      key: 'stats',
      header: 'Stats',
      render: (row) => {
        const opens = row.opens ?? 0;
        const clicks = row.clicks ?? 0;
        return (
          <div className="eac-stats-cell">
            <span className={`eac-stat-mini${opens > 0 ? ' has-value' : ''}`} title="Opens">
              <Mail className="h-2.5 w-2.5" />
              {' '}{opens}
            </span>
            <span className={`eac-stat-mini${clicks > 0 ? ' has-value' : ''}`} title="Clicks">
              <MousePointerClick className="h-2.5 w-2.5" />
              {' '}{clicks}
            </span>
          </div>
        );
      },
    },
    {
      key: 'leads',
      header: 'Lead',
      render: (row) => <ContactLeadCell leads={row.leads} />,
    },
    {
      key: 'addedDate',
      header: 'Entry Date',
      sortable: true,
      render: (row) => fmtDate(row.addedDate),
    },
  ], [totalSteps, stepFilter, selectedStep]);

  return (
    <ContactLeadOpener value={leadModal.openLead}>
      <style>{`
        .eac-badge {
          display: inline-block; padding: 3px 10px; border-radius: 12px;
          font-size: 12px; font-weight: 600; text-transform: capitalize;
        }
        .eac-badge-active { background: #d4edda; color: #155724; }
        .eac-badge-completed { background: #cce5ff; color: #004085; }
        .eac-badge-removed { background: #f8d7da; color: #721c24; }
        .eac-badge-unsubscribed { background: #fff3cd; color: #856404; }
        .eac-badge-invalid { background: #f8d0d4; color: #b02a37; }
        .eac-badge-failed { background: #e2e3e5; color: #5c636a; text-transform: none; }
        .eac-badge-default { background: #e9ecef; color: #666; }

        .eac-stats-cell { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
        .eac-stat-mini {
          display: inline-flex; align-items: center; gap: 3px;
          padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: 600;
          background: #f0f0f0; color: #999;
        }
        .eac-stat-mini.has-value { background: #e7f3ff; color: #00aff0; }
        .eac-pill {
          display: inline-block; padding: 2px 8px; border-radius: 10px; margin-left: 6px;
          font-size: 11px; font-weight: 600; background: #f1f5f9; color: #475569;
          white-space: nowrap;
        }

        /* ── Contact Stats Modal ── */
        .eac-overlay {
          display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 9999; justify-content: center; align-items: flex-start;
          padding-top: 60px; overflow-y: auto;
        }
        .eac-overlay.active { display: flex; }
        .eac-modal {
          background: #fff; border-radius: 10px; width: 90%; max-width: 720px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.25); margin-bottom: 40px;
        }
        .eac-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 18px 22px; border-bottom: 1px solid #e5e5e5;
        }
        .eac-modal-header h3 { margin: 0; font-size: 16px; font-weight: 600; }
        .eac-modal-close {
          background: none; border: none; font-size: 22px; cursor: pointer;
          color: #999; padding: 0 4px; line-height: 1;
        }
        .eac-modal-close:hover { color: #333; }
        .eac-modal-body { padding: 22px; }
        .eac-modal-loading { text-align: center; padding: 40px; color: #999; }

        .eac-contact-header {
          display: flex; justify-content: space-between; align-items: flex-start;
          margin-bottom: 20px;
        }
        .eac-contact-info { flex: 1; }
        .eac-contact-name { font-size: 18px; font-weight: 600; color: #333; margin: 0 0 4px; }
        .eac-contact-email { color: #666; font-size: 14px; margin: 0; }
        .eac-patient-link {
          display: inline-flex; align-items: center; gap: 5px;
          color: #00aff0; text-decoration: none; font-size: 13px;
          padding: 6px 12px; border: 1px solid #ddd; border-radius: 5px;
          background: white; white-space: nowrap; flex-shrink: 0;
          transition: background 0.2s;
        }
        .eac-patient-link:hover { background: #f5f5f5; text-decoration: none; }

        .eac-section { margin-bottom: 20px; }
        .eac-section-title {
          font-size: 13px; font-weight: 600; color: #333;
          text-transform: uppercase; letter-spacing: 0.5px;
          margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid #e5e5e5;
        }

        .eac-info-grid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 10px; margin-bottom: 16px;
        }
        .eac-info-item { background: #f8f9fa; border-radius: 8px; padding: 12px 14px; text-align: center; }
        .eac-info-item.warning { background: #fff5f5; }
        .eac-info-label { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #999; margin-bottom: 4px; }
        .eac-info-value { font-size: 13px; font-weight: 600; color: #333; }

        .eac-engagement { display: flex; gap: 12px; }
        .eac-stat-box {
          flex: 1; text-align: center; padding: 16px;
          background: #f8f9fa; border-radius: 8px;
        }
        .eac-stat-number { font-size: 28px; font-weight: 700; margin-bottom: 2px; }
        .eac-stat-number.opens { color: #28a745; }
        .eac-stat-number.clicks { color: #ffc107; }
        .eac-stat-number.zero { color: #ccc; }
        .eac-stat-label-lg { font-size: 11px; color: #888; font-weight: 600; text-transform: uppercase; }
      `}</style>

      <div className="flex flex-col h-full overflow-hidden p-6">
        {/* Header */}
        <div className="flex-shrink-0 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link
                to={`/marketing/automations/email/${automationId}/stats`}
                className="p-1 rounded hover:bg-slate-100"
              >
                <ArrowLeft className="h-5 w-5 text-slate-500" />
              </Link>
              <h1 className="text-xl font-semibold text-slate-800">Automation Contacts</h1>
              <div className="flex items-center gap-2 ml-2">
                <span className="text-2xl font-bold text-blue-500">{fmtNum(counts.total)}</span>
                <span className="text-sm font-semibold text-slate-500">{counts.total === 1 ? 'contact' : 'contacts'}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/marketing/automations/email/${automationId}/stats`}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Automation Stats
              </Link>
            </div>
          </div>

          {/* Status, leads and step/outcome filters + search */}
          <div className="flex flex-wrap items-center gap-4">
            <FilterSelect
              options={FILTER_OPTIONS}
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
              label="Filter by status"
              className="w-60"
            />
            <ContactLeadFilter
              value={leadFilter}
              onChange={(v) => { setLeadFilter(v); setPage(1); }}
              total={counts.total}
              leadCount={leadCount}
              recipientCount={contactsData?.leadRecipientCount}
              referralCount={contactsData?.leadReferralCount}
            />
            <FilterSelect
              options={STEP_OPTIONS}
              value={stepFilter}
              onChange={(v) => applySendFilters(v, outcomeFilter)}
              label="Step"
              className="w-48"
            />
            <FilterSelect
              options={OUTCOME_OPTIONS}
              value={outcomeFilter}
              onChange={(v) => applySendFilters(stepFilter, v)}
              label="Outcome"
              className="w-44"
            />
            <div className="ml-auto">
              <Input
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search contacts..."
                className="w-64"
                leftAddon={<Search className="h-4 w-4" />}
              />
            </div>
          </div>

          {/* Contacts vs tries: the filtered view says outright which one it shows. */}
          {(stepFilter || outcomeFilter) && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span>
                <strong>{fmtNum(paginationTotal)}</strong> contact{paginationTotal === 1 ? '' : 's'}
                {selectedStep ? ` with a ${stepLabel(selectedStep)} send` : ' with a send'}
                {outcomeFilter in OUTCOME_PHRASE ? ` ${OUTCOME_PHRASE[outcomeFilter as EmailStepOutcome]}` : ''}
                {selectedStep && outcomeFilter === 'failed' && selectedStep.failedAttempts !== paginationTotal
                  ? ` — the step card counts ${fmtNum(selectedStep.failedAttempts)} failed tries, retries included`
                  : ''}
                .
              </span>
              <button type="button" className="underline hover:text-gray-900" onClick={() => applySendFilters('', '')}>
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 mt-4">
          <DataTable<AutomationContact>
            data={contacts}
            columns={columns}
            keyField="contactId"
            loading={isLoading}
            page={page}
            pageSize={perPage}
            total={paginationTotal}
            onPageChange={setPage}
            onRowClick={(row) => openContactStats(row)}
          />
        </div>
      </div>

      {/* ── Contact Stats Modal ── */}
      <div
        className={`eac-overlay${modalOpen && !leadModal.isOpen ? ' active' : ''}`}
        onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
      >
        <div className="eac-modal">
          <div className="eac-modal-header">
            <h3>Contact Stats</h3>
            <button className="eac-modal-close" onClick={closeModal}>&times;</button>
          </div>
          <div className="eac-modal-body">
            {modalLoading && <div className="eac-modal-loading"><Loader2 className="h-6 w-6 animate-spin inline-block" /> Loading...</div>}
            {!modalLoading && !modalData && modalOpen && <div className="eac-modal-loading">Failed to load contact stats.</div>}
            {!modalLoading && modalData && <ContactStatsContent data={modalData} totalSteps={totalSteps} tz={userTz} />}
          </div>
        </div>
      </div>

      {leadModal.modal}
    </ContactLeadOpener>
  );
}

// ─── Contact Stats Modal Content ─────────────────────────────────────────────

function ContactStatsContent({ data, totalSteps, tz }: { data: AutomationContactStatsData; totalSteps: number; tz: string }) {
  const c = data.contact;
  const steps = data.steps ?? [];
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ');

  let totalOpens = 0;
  let totalClicks = 0;
  for (const s of steps) {
    totalOpens += Number(s.open) || 0;
    totalClicks += Number(s.clicks) || 0;
  }

  const statusBadgeClass = STATUS_BADGE_CLASS[c.status] || 'eac-badge-default';

  return (
    <>
      {/* Why a stopped contact stopped — first thing in the modal. Renders
          nothing for an active or completed contact. */}
      <EmailAutomationStopNotice contact={c} tz={tz} className="mb-5" />

      {/* Contact header */}
      <div className="eac-contact-header">
        <div className="eac-contact-info">
          {name && <p className="eac-contact-name">{name}</p>}
          <p className="eac-contact-email">{c.email}</p>
        </div>
        {c.patientId && (
          <Link to={`/patients/${c.patientId}`} className="eac-patient-link">
            <User className="h-3 w-3" />
            View Patient
          </Link>
        )}
      </div>

      {/* Contact info */}
      <div className="eac-section">
        <div className="eac-section-title">Contact Information</div>
        <div className="eac-info-grid">
          <div className="eac-info-item">
            <div className="eac-info-label">Status</div>
            <div className="eac-info-value"><span className={`eac-badge ${statusBadgeClass}`}>{statusLabel(c.status)}</span></div>
          </div>
          <div className="eac-info-item">
            <div className="eac-info-label">Entry Date</div>
            <div className="eac-info-value">{fmtDate(c.addedDate)}</div>
          </div>
          {c.status === 'completed' && c.completedDate && (
            <div className="eac-info-item">
              <div className="eac-info-label">Completed Date</div>
              <div className="eac-info-value">{fmtDate(c.completedDate)}</div>
            </div>
          )}
          {c.status === 'removed' && c.removedDate && (
            <div className="eac-info-item warning">
              <div className="eac-info-label">Removed Date</div>
              <div className="eac-info-value">{fmtDate(c.removedDate)}</div>
            </div>
          )}
          <div className="eac-info-item">
            <div className="eac-info-label">Current Step</div>
            <div className="eac-info-value">{c.currentStepOrder || 0} / {totalSteps}</div>
          </div>
        </div>
      </div>

      {/* Overall engagement */}
      <div className="eac-section">
        <div className="eac-section-title">Overall Engagement</div>
        <div className="eac-engagement">
          <div className="eac-stat-box">
            <div className={`eac-stat-number ${totalOpens > 0 ? 'opens' : 'zero'}`}>{totalOpens}</div>
            <div className="eac-stat-label-lg">Total Opens</div>
          </div>
          <div className="eac-stat-box">
            <div className={`eac-stat-number ${totalClicks > 0 ? 'clicks' : 'zero'}`}>{totalClicks}</div>
            <div className="eac-stat-label-lg">Total Clicks</div>
          </div>
        </div>
      </div>

      {/* Every attempt — sent, failed and held — with SendGrid's message.
          Repeats of the same failure on a step collapse into one line. */}
      <div className="eac-section">
        <div className="eac-section-title">Send History</div>
        <EmailAutomationSendHistory sends={steps} leads={data.leads ?? []} tz={tz} />
      </div>
    </>
  );
}
